import { useState } from "react";
import { Box, Button, Typography, useTheme } from "@mui/material";
import WhatsAppIcon from "@/modules/common/components/ui-kit/icons/whatsapp-icon";
import WhatsappQrModal from "@/modules/common/components/whatsapp-relink/whatsapp-qr-modal";
import useDraggable from "@/modules/common/hooks/use-draggable.hook";
import useScreenSize from "@/modules/common/hooks/device-detection.hook";

interface WhatsappWarningBannerProps {
  gracePeriodMinutes: number | null;
}

const formatGracePeriod = (minutes: number | null): string | null => {
  if (minutes === null || minutes <= 0) return null;
  if (minutes >= 60) {
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return m > 0 ? `${h}h ${m}m remaining` : `${h}h remaining`;
  }
  return `${minutes}m remaining`;
};

const WhatsappWarningBanner = ({
  gracePeriodMinutes,
}: WhatsappWarningBannerProps) => {
  const theme = useTheme();
  const { isMobile } = useScreenSize();
  const [qrOpen, setQrOpen] = useState(false);
  const { dragRef, isDragging, hasDragged, dragHandlers, positionStyles } =
    useDraggable({ isMobile });

  const redColor = theme.nawy.colors.semantic.indicator.red;

  return (
    <>
      <Box
        ref={dragRef}
        {...(isMobile ? {} : dragHandlers)}
        sx={{
          position: "fixed",
          zIndex: 1300,
          userSelect: "none",
          touchAction: "none",
          ...(isMobile
            ? {
                left: 8,
                right: 8,
                bottom: 12,
                cursor: "default",
              }
            : {
                cursor: isDragging ? "grabbing" : "grab",
                ...positionStyles,
              }),
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: theme.nawy.spacing.sm,
            pl: theme.nawy.spacing.sm,
            pr: theme.nawy.spacing.sm,
            py: theme.nawy.spacing.xs,
            backgroundColor: redColor,
            borderRadius: "8px",
            boxShadow: "0 6px 20px rgba(0,0,0,0.15)",
            width: isMobile ? "100%" : "auto",
            minWidth: isMobile ? 0 : 280,
          }}
        >
          <WhatsAppIcon size={20} color="#fff" />

          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Typography
              sx={{
                ...theme.nawy.typography.english.variants.body.sm.regular,
                color: "#fff",
                lineHeight: 1.2,
              }}
            >
              Whatsapp isn&apos;t synced
            </Typography>
            {formatGracePeriod(gracePeriodMinutes) && (
              <Typography
                sx={{
                  fontSize: 11,
                  lineHeight: 1.2,
                  color: "rgba(255,255,255,0.85)",
                }}
              >
                {formatGracePeriod(gracePeriodMinutes)}
              </Typography>
            )}
          </Box>

          <Button
            onClick={(e) => {
              e.stopPropagation();
              if (hasDragged.current) return;
              setQrOpen(true);
            }}
            disableRipple
            sx={{
              textTransform: "none",
              minWidth: "auto",
              p: 0,
              ...theme.nawy.typography.english.variants.body.sm.semibold,
              color: "#fff",
              "&:hover": { backgroundColor: "transparent", opacity: 0.85 },
            }}
          >
            Sync now
          </Button>
        </Box>
      </Box>

      <WhatsappQrModal
        open={qrOpen}
        onClose={() => setQrOpen(false)}
        isBlocked={false}
      />
    </>
  );
};

export default WhatsappWarningBanner;
